import type { Paginated } from "@kansai-ai-hub/types";

import { apiClient } from "@/lib/api-client";

/** サーバの 1 ページあたり最大件数（schema 側の pageSize.max と一致させる）。 */
const MAX_PAGE_SIZE = 100;

/**
 * 全件取得時に並列で叩く追加ページ数の上限。
 * データが想定外に膨らんでも、ブラウザが一度に数百〜数千リクエストを投げて
 * フリーズ／レート制限に当たるのを防ぐ安全弁。上限に当たった場合は切り詰めたことを
 * 明示し（「黙って打ち切らない」原則）、呼び出し側の UI もページング前提で破綻しない。
 * 100 件/ページ × 50 ページ = 最大 5,000 件まで。
 */
const MAX_PAGES = 50;

/**
 * ページング API から **全件**を取得して 1 つの `Paginated<T>` にまとめる。
 *
 * クライアント側でグルーピングや再ページネーションを行う画面（応募管理のネスト表示・
 * 案件詳細の応募一覧など）で、サーバのページ境界によってデータが分断・欠落するのを防ぐ。
 * 1 ページ目で総件数を把握し、残ページを並列取得する。
 *
 * 返り値の `meta.total` は **サーバの真の総件数** を返す（「全 N 件」表示が嘘にならないよう、
 * `MAX_PAGES` で items を切り詰めた場合でも total は実数のまま）。`page` / `totalPages` は 1 に
 * 正規化し、呼び出し側はこれを単一リストとして扱える。切り詰めが起きたかは `items.length < total`
 * で判定できる（切り詰め時は DEV で warn も出す）。
 */
export async function fetchAllPaginated<T>(
  path: string,
  query: Record<string, unknown> = {},
  pageSize = MAX_PAGE_SIZE,
  maxPages = MAX_PAGES,
): Promise<Paginated<T>> {
  const first = await apiClient.get<Paginated<T>>(path, {
    query: { ...query, page: 1, pageSize },
  });
  const total = first.meta.total;
  if (first.items.length >= total) {
    return { items: first.items, meta: { ...first.meta, page: 1, pageSize: total, totalPages: 1 } };
  }

  const totalPages = Math.ceil(total / pageSize);
  const cappedPages = Math.min(totalPages, maxPages);
  if (cappedPages < totalPages && import.meta.env.DEV) {
    console.warn(
      `[fetchAllPaginated] ${path}: 総 ${total} 件 (${totalPages} ページ) のうち先頭 ` +
        `${cappedPages} ページ (${cappedPages * pageSize} 件) のみ取得して打ち切りました。` +
        `MAX_PAGES (${maxPages}) を超えています。`,
    );
  }
  const rest = await Promise.all(
    Array.from({ length: cappedPages - 1 }, (_, i) =>
      apiClient.get<Paginated<T>>(path, { query: { ...query, page: i + 2, pageSize } }),
    ),
  );
  const items = [...first.items, ...rest.flatMap((r) => r.items)];
  return { items, meta: { total, page: 1, pageSize: items.length, totalPages: 1 } };
}
